// Angular
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// Libraries

import { Select, Store } from '@ngxs/store';

// Services
import { AppCleanup } from '../../../shared/models';

// Models
import { JobCommentDefaultDto } from '../models/job-comment-default-dto';
import { JobCommentDefaultGroupDto, JobCommentDefaultGroupLocalStorage } from '../models/job-comment-default-group-dto';
import { JobCommentDefaultLinkDto } from '../models/job-comment-default-link-dto';
import { JobCommentDefaultResponseAllDto } from '../models/job-comment-default-response-all-dto';
import { JobCommentChannelDto } from '../../job/models/job-comment-channel-dto';
import { JobTypeChannelMappingDto } from '../../job/models/job-type-channel-mapping-dto.model';

// Store
import { AuthState } from '../../auth/state/auth.state';
import { CommentDataState } from '../state/comment.state';
import { CommentDataActions } from '../state/comment.action';

@Injectable({
	providedIn: 'root',
})
export class JobCommentService extends AppCleanup {
	@Select(AuthState.isLoggedIn) isLoggedIn$: Observable<boolean>;
	@Select(CommentDataState.isLoaded) commentDataLoaded$: Observable<boolean>;
	@Select(CommentDataState.commentData) commentData$: Observable<JobCommentDefaultResponseAllDto>;

	private readonly jobCommentUrl: string = `${this.apiUrl}api/jobcomment`;

	jobCommentDefaultGroupsForLocalStorage?: JobCommentDefaultGroupLocalStorage[] = [];

	protected jobCommentPopUpTriggerVar: Subject<JobCommentDefaultDto> = new Subject<JobCommentDefaultDto>();
	jobCommentPopUpTrigger$ = this.jobCommentPopUpTriggerVar.asObservable();

	constructor(
		private http: HttpClient,
		private store: Store
	) {
		super();
		this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((isLoggedIn) => {
			// No need for comment groups to initialize on public links
			if (isLoggedIn) {
				this.commentDataLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
					if (!isLoaded) {
						this.store.dispatch(new CommentDataActions.GetAll({ showSpinner: false, showToast: false }));
					}
				});
			}
		});
	}

	getCommentGroups() {
		return this.http.get<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/groups`);
	}

	// Comment Default Group

	createCommentDefaultGroup(jobCommentDefaultGroupDto: JobCommentDefaultGroupDto) {
		return this.http.post<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/groups`, jobCommentDefaultGroupDto);
	}

	updateCommentDefaultGroup(jobCommentDefaultGroupDto: JobCommentDefaultGroupDto) {
		return this.http.put<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/groups`, jobCommentDefaultGroupDto);
	}

	deleteCommentDefaultGroup(jobCommentDefaultGroupDto: JobCommentDefaultGroupDto) {
		return this.http.delete<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/groups/${jobCommentDefaultGroupDto.id}`);
	}

	updateCommentGroupSequence(jobCommentDefaultGroupDtos: JobCommentDefaultGroupDto[]) {
		return this.http.put<JobCommentDefaultGroupDto[]>(`${this.jobCommentUrl}/groups/seq`, jobCommentDefaultGroupDtos);
	}

	// Comment Default

	createCommentDefault(jobCommentDefaultDto: JobCommentDefaultDto) {
		return this.http.post<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/groups/default`, jobCommentDefaultDto);
	}

	updateCommentDefault(jobCommentDefaultDto: JobCommentDefaultDto) {
		return this.http.put<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/groups/default`, jobCommentDefaultDto);
	}

	deleteCommentDefault(jobCommentDefaultDto: JobCommentDefaultDto) {
		return this.http.delete<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/groups/default/${jobCommentDefaultDto.id}`);
	}

	updateCommentSequence(jobCommentDefaultDtos: JobCommentDefaultDto[]) {
		return this.http.put<JobCommentDefaultDto[]>(`${this.jobCommentUrl}/groups/default/seq`, jobCommentDefaultDtos);
	}

	// Comment Links

	createJobCommentDefaultLink(jobCommentDefaultLinkDto: JobCommentDefaultLinkDto) {
		return this.http.post<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/link`, jobCommentDefaultLinkDto);
	}

	updateJobCommentDefaultLink(jobCommentDefaultLinkDto: JobCommentDefaultLinkDto) {
		return this.http.put<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/link`, jobCommentDefaultLinkDto);
	}

	deleteJobCommentDefaultLink(jobCommentDefaultLinkDto: JobCommentDefaultLinkDto) {
		return this.http.delete<JobCommentDefaultResponseAllDto>(`${this.jobCommentUrl}/link/${jobCommentDefaultLinkDto.id}`);
	}

	sendJobCommentMessage(jobCommentDefault: JobCommentDefaultDto) {
		this.jobCommentPopUpTriggerVar.next(jobCommentDefault);
	}

	// Others

	getManualEntryValuesForJob(jobId: number, currentDate: number) {
		return this.http.get<JobCommentChannelDto[]>(`${this.jobCommentUrl}/channel-value/${jobId}?unixCommentDate=${currentDate}`);
	}

	addJobTypeChannelMapping(jobTypeChannelMapping: JobTypeChannelMappingDto) {
		return this.http.post<JobTypeChannelMappingDto>(`${this.jobCommentUrl}/channel-override`, jobTypeChannelMapping);
	}

	editJobTypeChannelMapping(jobTypeChannelMapping: JobTypeChannelMappingDto) {
		return this.http.put<JobTypeChannelMappingDto>(`${this.jobCommentUrl}/channel-override`, jobTypeChannelMapping);
	}

	deleteJobTypeChannelMapping(jobTypeChannelMapping: JobTypeChannelMappingDto) {
		return this.http.delete(`${this.jobCommentUrl}/channel-override/${jobTypeChannelMapping.id}`);
	}
}
